import React, { useEffect, useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { Text } from '@cotera/client/app/components/ui';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
import { asRecord, readString } from '../../utils';

const {
  FILTER_OPERATORS,
  FILTER_OPERATOR_LABELS,
  FILTER_OPERATORS_REQUIRING_VALUE,
  FILTER_OPERATORS_REQUIRING_COMPARABLE,
  FILTER_OPERATORS_REQUIRING_LIST,
  isFilterOperator,
  isFilterCombinator,
  isComparableFilterValue,
  filterFieldPointer,
  isNodeScopedFilterPath,
} = PersistedWorkflow;

type FilterOperator = PersistedWorkflow.FilterOperator;
type FilterCombinator = PersistedWorkflow.FilterCombinator;

/**
 * A save-ready filter config. Mutable arrays because this is a wire payload —
 * it goes straight into node `data` / a trigger's `filter` column.
 */
export type FilterConditionsConfig = {
  combinator: FilterCombinator;
  groups: {
    combinator: FilterCombinator;
    conditions: { path: string; operator: FilterOperator; value?: string }[];
  }[];
};

type ConditionRow = {
  readonly id: string;
  readonly path: string;
  readonly operator: FilterOperator;
  readonly value: string;
};

type GroupRow = {
  readonly id: string;
  readonly combinator: FilterCombinator;
  readonly conditions: readonly ConditionRow[];
};

function conditionFromRecord(
  record: Record<string, unknown> | null,
  id: string
): ConditionRow {
  const savedOperator = record?.['operator'];
  return {
    id,
    path: readString(record, 'path') ?? '',
    operator: isFilterOperator(savedOperator) ? savedOperator : 'exists',
    value: readString(record, 'value') ?? '',
  };
}

/**
 * Reads a persisted filter config into editable groups, accepting every shape:
 * `{ combinator, groups: [{ combinator, conditions }] }` (current), `{ conditions }`
 * (single AND group), and the legacy `{ path, operator, value }`. Missing
 * combinators default to OR between groups and AND within a group.
 */
export function readFilterGroups(data: Record<string, unknown> | null): {
  combinator: FilterCombinator;
  groups: GroupRow[];
  nextId: number;
} {
  let nextId = 1;
  const makeId = (): string => {
    const id = `row-${nextId}`;
    nextId += 1;
    return id;
  };

  const rawGroups = data?.['groups'];
  const rawConditions = data?.['conditions'];

  let groupSources: { combinator: FilterCombinator; conditions: unknown[] }[];
  if (Array.isArray(rawGroups) && rawGroups.length > 0) {
    groupSources = rawGroups.map((group) => {
      const groupRecord = asRecord(group);
      const groupConditions = groupRecord?.['conditions'];
      const savedCombinator = groupRecord?.['combinator'];
      return {
        combinator: isFilterCombinator(savedCombinator)
          ? savedCombinator
          : 'and',
        conditions: Array.isArray(groupConditions) ? groupConditions : [],
      };
    });
  } else if (Array.isArray(rawConditions) && rawConditions.length > 0) {
    groupSources = [{ combinator: 'and', conditions: rawConditions }];
  } else if (data !== null && data['operator'] !== undefined) {
    groupSources = [{ combinator: 'and', conditions: [data] }];
  } else {
    groupSources = [{ combinator: 'and', conditions: [] }];
  }

  const groups: GroupRow[] = groupSources.map((source) => {
    const rows =
      source.conditions.length > 0
        ? source.conditions.map((raw) =>
            conditionFromRecord(asRecord(raw), makeId())
          )
        : [{ id: makeId(), path: '', operator: 'exists' as const, value: '' }];
    return { id: makeId(), combinator: source.combinator, conditions: rows };
  });

  const savedTopCombinator = data?.['combinator'];
  const combinator = isFilterCombinator(savedTopCombinator)
    ? savedTopCombinator
    : 'or';

  return { combinator, groups, nextId };
}

function rowValueError(row: ConditionRow): boolean {
  if (!FILTER_OPERATORS_REQUIRING_COMPARABLE.has(row.operator)) {
    return false;
  }
  // Only flag once the user has typed something un-comparable; a blank value is
  // covered by the generic "value required" gate.
  return row.value.trim() !== '' && !isComparableFilterValue(row.value);
}

/**
 * Turns the editable rows into a save-ready config, or null while any condition
 * is incomplete. Kept separate from the component so it can be tested directly.
 */
export function buildFilterConditionsConfig(
  combinator: FilterCombinator,
  groups: readonly GroupRow[]
): FilterConditionsConfig | null {
  if (groups.length === 0) {
    return null;
  }
  const outGroups: {
    combinator: FilterCombinator;
    conditions: { path: string; operator: FilterOperator; value?: string }[];
  }[] = [];
  for (const group of groups) {
    if (group.conditions.length === 0) {
      return null;
    }
    const conditions: {
      path: string;
      operator: FilterOperator;
      value?: string;
    }[] = [];
    for (const row of group.conditions) {
      // Field may be a `{{ref}}` token or a typed pointer; validate the
      // resolved bare pointer / node-scoped ref, but persist the raw field so
      // pills survive.
      const fieldPath = row.path.trim();
      const ref = filterFieldPointer(fieldPath);
      if (ref !== '' && !ref.startsWith('/') && !isNodeScopedFilterPath(ref)) {
        return null;
      }
      const requiresValue = FILTER_OPERATORS_REQUIRING_VALUE.has(row.operator);
      const requiresComparable = FILTER_OPERATORS_REQUIRING_COMPARABLE.has(
        row.operator
      );
      if (requiresValue && row.value.trim() === '') {
        return null;
      }
      if (requiresComparable && !isComparableFilterValue(row.value)) {
        return null;
      }
      const condition: {
        path: string;
        operator: FilterOperator;
        value?: string;
      } = { path: fieldPath, operator: row.operator };
      if (requiresValue) {
        condition.value = row.value;
      }
      conditions.push(condition);
    }
    outGroups.push({ combinator: group.combinator, conditions });
  }
  return { combinator, groups: outGroups };
}

/** A small tappable AND/OR pill. Clicking flips the combinator it represents. */
const CombinatorPill: React.FC<{
  value: FilterCombinator;
  onToggle: () => void;
}> = ({ value, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    title="Toggle AND / OR"
    className="text-muted-foreground hover:bg-accent hover:text-foreground rounded bg-muted px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide transition-colors"
  >
    {value === 'and' ? 'AND' : 'OR'}
  </button>
);

/** An AND/OR pill sitting on a connector line, shown between conditions or groups. */
const CombinatorDivider: React.FC<{
  value: FilterCombinator;
  onToggle: () => void;
}> = ({ value, onToggle }) => (
  <div className="flex items-center gap-2">
    <CombinatorPill value={value} onToggle={onToggle} />
    <div className="bg-border h-px flex-1" />
  </div>
);

/**
 * Two-level AND/OR condition builder. Conditions within a group combine by the
 * group's pill; groups combine by the top-level pill — so `(x OR y) AND (w OR z)`
 * and `(a AND b) OR (c AND d)` are both expressible.
 *
 * What varies between hosts is only how a *field* is chosen: a filter node picks
 * JSON pointers out of its upstream node's output, while a dataset trigger picks
 * a column. That is the `renderFieldInput` prop; everything else — operators,
 * value coercion, validation — is shared so the two cannot drift.
 */
export const FilterConditionsBuilder: React.FC<{
  /** Persisted config to seed from. */
  initial: unknown;
  /** Changing this re-seeds local state (e.g. switching to a different node). */
  seedKey: string;
  /** Called with a save-ready config, or null while any condition is incomplete. */
  onChange: (config: FilterConditionsConfig | null) => void;
  renderFieldInput: (props: {
    value: string;
    onChange: (next: string) => void;
  }) => React.ReactNode;
  heading: string;
}> = ({ initial, seedKey, onChange, renderFieldInput, heading }) => {
  const seeded = readFilterGroups(asRecord(initial));
  const [combinator, setCombinator] = useState<FilterCombinator>(
    seeded.combinator
  );
  const [groups, setGroups] = useState<GroupRow[]>(seeded.groups);
  const [nextRowId, setNextRowId] = useState(seeded.nextId);

  // Re-seed when the host switches targets so state never leaks across them.
  const [seededKey, setSeededKey] = useState(seedKey);
  if (seededKey !== seedKey) {
    setSeededKey(seedKey);
    const next = readFilterGroups(asRecord(initial));
    setCombinator(next.combinator);
    setGroups(next.groups);
    setNextRowId(next.nextId);
  }

  useEffect(() => {
    onChange(buildFilterConditionsConfig(combinator, groups));
  }, [combinator, groups, onChange]);

  const allocateId = (): string => {
    const id = `row-${nextRowId}`;
    setNextRowId((n) => n + 1);
    return id;
  };

  const toggleTopCombinator = (): void =>
    setCombinator((c) => (c === 'and' ? 'or' : 'and'));

  const toggleGroupCombinator = (groupId: string): void => {
    setGroups((prev) =>
      prev.map((group) =>
        group.id === groupId
          ? { ...group, combinator: group.combinator === 'and' ? 'or' : 'and' }
          : group
      )
    );
  };

  const updateCondition = (
    groupId: string,
    conditionId: string,
    patch: Partial<Pick<ConditionRow, 'path' | 'operator' | 'value'>>
  ): void => {
    setGroups((prev) =>
      prev.map((group) =>
        group.id === groupId
          ? {
              ...group,
              conditions: group.conditions.map((row) =>
                row.id === conditionId ? { ...row, ...patch } : row
              ),
            }
          : group
      )
    );
  };

  const removeCondition = (groupId: string, conditionId: string): void => {
    setGroups((prev) =>
      prev.map((group) =>
        group.id === groupId
          ? {
              ...group,
              conditions: group.conditions.filter(
                (row) => row.id !== conditionId
              ),
            }
          : group
      )
    );
  };

  const addCondition = (groupId: string): void => {
    const id = allocateId();
    setGroups((prev) =>
      prev.map((group) =>
        group.id === groupId
          ? {
              ...group,
              conditions: [
                ...group.conditions,
                { id, path: '', operator: 'exists', value: '' },
              ],
            }
          : group
      )
    );
  };

  const removeGroup = (groupId: string): void => {
    setGroups((prev) => prev.filter((group) => group.id !== groupId));
  };

  const addGroup = (): void => {
    const conditionId = allocateId();
    const groupId = allocateId();
    setGroups((prev) => [
      ...prev,
      {
        id: groupId,
        combinator: 'and',
        conditions: [
          { id: conditionId, path: '', operator: 'exists', value: '' },
        ],
      },
    ]);
  };

  const multiGroup = groups.length > 1;
  const hasCombinatorPills =
    multiGroup || groups.some((group) => group.conditions.length > 1);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <Text.Caption className="text-foreground block font-medium">
          {heading}
        </Text.Caption>
        {hasCombinatorPills ? (
          <Text.Caption className="text-muted-foreground/70 block">
            Click AND / OR to change how they combine.
          </Text.Caption>
        ) : null}
      </div>

      {groups.map((group, groupIndex) => (
        <React.Fragment key={group.id}>
          {groupIndex > 0 ? (
            <CombinatorDivider
              value={combinator}
              onToggle={toggleTopCombinator}
            />
          ) : null}

          <div className="border-l-foreground/20 flex flex-col gap-2 border-l-2 py-1 pl-3">
            {multiGroup ? (
              <div className="-mt-1 flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground h-6 w-6 shrink-0"
                  title="Remove group"
                  onClick={() => removeGroup(group.id)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : null}

            {group.conditions.map((row, index) => {
              const requiresValue = FILTER_OPERATORS_REQUIRING_VALUE.has(
                row.operator
              );
              const requiresComparable =
                FILTER_OPERATORS_REQUIRING_COMPARABLE.has(row.operator);
              const requiresList = FILTER_OPERATORS_REQUIRING_LIST.has(
                row.operator
              );
              const valueError = rowValueError(row);
              return (
                <React.Fragment key={row.id}>
                  {index > 0 ? (
                    <CombinatorDivider
                      value={group.combinator}
                      onToggle={() => toggleGroupCombinator(group.id)}
                    />
                  ) : null}

                  <div className="flex flex-col gap-2">
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        {renderFieldInput({
                          value: row.path,
                          onChange: (path) =>
                            updateCondition(group.id, row.id, { path }),
                        })}
                      </div>
                      {group.conditions.length > 1 ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground h-9 w-8 shrink-0"
                          title="Remove condition"
                          onClick={() => removeCondition(group.id, row.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>

                    <div className="flex items-start gap-2">
                      <Select
                        value={row.operator}
                        onValueChange={(next) => {
                          if (isFilterOperator(next)) {
                            updateCondition(group.id, row.id, {
                              operator: next,
                            });
                          }
                        }}
                      >
                        <SelectTrigger className="h-9 flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FILTER_OPERATORS.map((op) => (
                            <SelectItem key={op} value={op}>
                              {FILTER_OPERATOR_LABELS[op]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {requiresValue ? (
                        <Input
                          value={row.value}
                          className="flex-1"
                          placeholder={
                            requiresList
                              ? 'web, email, api'
                              : requiresComparable
                              ? '5  or  2025-11-01'
                              : 'value'
                          }
                          aria-invalid={valueError}
                          onChange={(event) =>
                            updateCondition(group.id, row.id, {
                              value: event.target.value,
                            })
                          }
                        />
                      ) : null}
                    </div>

                    {requiresValue && requiresList ? (
                      <Text.Caption className="text-muted-foreground/70 block">
                        Comma-separated, or a JSON array.
                      </Text.Caption>
                    ) : null}
                    {valueError ? (
                      <Text.Caption className="text-destructive block">
                        Must be a number or date.
                      </Text.Caption>
                    ) : null}
                  </div>
                </React.Fragment>
              );
            })}

            <Button
              type="button"
              variant="ghost"
              className="text-muted-foreground h-8 justify-start gap-2 self-start px-2"
              onClick={() => addCondition(group.id)}
            >
              <Plus className="h-4 w-4" />
              Add condition
            </Button>
          </div>
        </React.Fragment>
      ))}

      <Button
        type="button"
        variant="ghost"
        className="text-muted-foreground h-8 justify-start gap-2 self-start px-2"
        onClick={addGroup}
      >
        <Plus className="h-4 w-4" />
        Add another group
      </Button>
    </div>
  );
};
